import { format, getYear, isToday, isYesterday } from 'date-fns';
import pickBy from 'lodash/pickBy';
import jwtDecode from 'jwt-decode';
import { getTheme } from '@Thread-Magic/jasmine';
import { CONVERSATION_SOURCES, EXTERNAL_SESSION_TYPES } from '../constants';

export const isElectron = () => window?.navigator?.userAgent?.toLowerCase()?.includes('electron');

export const isDevelopment = () => process.env.NODE_ENV === 'development';

export const isProduction = () => process.env.NODE_ENV === 'production';

export const isAdaptiveCard = str => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return !!JSON.parse(str).template_name;
};

export const getHeaders = (getState, headers) => {
  const { user } = getState().users;

  return {
    Authorization: `Bearer ${user.token}`,
    Accept: 'application/vnd.chatgenie.test+json;version=1',
    ...(user.appId && { 'APP-ID': user.appId }),
    ...headers
  };
};

// https://stackoverflow.com/questions/6312993/javascript-seconds-to-time-string-with-format-hhmmss
export function toHHMMSS(timeSeconds) {
  let hours = Math.floor(timeSeconds / 3600);
  let minutes = Math.floor((timeSeconds - hours * 3600) / 60);
  let seconds = timeSeconds - hours * 3600 - minutes * 60;

  if (hours < 10) {
    hours = `0${hours}`;
  }
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${hours} : ${minutes} : ${seconds}`;
}

export const setLocalStorage = data => {
  Object.keys(data).forEach(item => {
    localStorage[item] = JSON.stringify({ data: data[item] });
  });
};

export const getLocalStorage = key => {
  const storageItem = JSON.parse(localStorage.getItem(key));
  return storageItem && storageItem.data;
};

export const setSessionStorage = data => {
  Object.keys(data).forEach(item => {
    sessionStorage[item] = JSON.stringify({ data: data[item] });
  });
};

export const getSessionStorage = key => {
  const storageItem = JSON.parse(sessionStorage.getItem(key));
  return storageItem && storageItem.data;
};

export const noop = () => {};
export const getUrlParam = (param, string) => {
  let queryString = string || window.location.search;

  if (isElectron() && window.location.hash) {
    queryString = window.location.hash.split('?')[1] || '';
  }

  return queryString
    .replace('?', '')
    .split('&')
    .reduce((accumulator, currentValue) => {
      const a = currentValue.split('=');
      accumulator[decodeURIComponent(a[0])] = decodeURIComponent(a[1]);
      return accumulator;
    }, {})[param];
};

export const compareOnKey = key => (a, b) => {
  if (a[key] < b[key]) {
    return -1;
  }

  if (a[key] > b[key]) {
    return 1;
  }

  return 0;
};

export const getRelativeDate = date => {
  switch (true) {
    case isToday(date):
      return 'Today';
    case isYesterday(date):
      return 'Yesterday';
    case getYear(date) === getYear(new Date()) - 1:
      return format(date, 'MMMM dd yyyy');
    default:
      return format(date, 'MMMM dd');
  }
};

export const isMacintosh = () => window?.navigator.platform.indexOf('Mac') > -1;
export const isWindows = () => window?.navigator.platform.indexOf('Win32') > -1;

export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.indexOf('safari') !== -1) {
    if (ua.indexOf('chrome') > -1) return false;
  }
  return true;
};

export const isInputTypeElement = event => {
  const nodeName = event.nodeName.toLowerCase();
  if (
    event.nodeType === 1 &&
    (nodeName === 'textarea' || nodeName === 'input' || event.isContentEditable)
  ) {
    return true;
  }
  return false;
};
export const removeInvalidQueries = queries =>
  pickBy(queries, val => ![undefined, null, ''].includes(val));

export const isScrollable = scrollElement => {
  const nodeElement =
    typeof scrollElement === 'string' ? document.getElementById(scrollElement) : scrollElement;

  if (!nodeElement) {
    return false;
  }
  return nodeElement.scrollHeight > nodeElement.clientHeight;
};

export const getExternalSessionType = () => {
  const { pathname } = window.location;

  if (pathname.startsWith('/connectwise')) {
    return EXTERNAL_SESSION_TYPES.CONNECTWISE;
  }
  if (pathname.startsWith('/autotask')) {
    return EXTERNAL_SESSION_TYPES.AUTOTASK;
  }
  return null;
};

export const isExternalSession = () => getExternalSessionType() !== null;

export const getSessionApp = () => {
  const prefix = 'inbox';

  const type = isElectron() ? 'desktop' : getExternalSessionType() || 'web';

  return `${prefix}-${type}`;
};

export const isSmallScreen = () => window.innerWidth < 576;

export const isTokenAboutExpire = (token, deltaMinutes = 15) => {
  const jwt = jwtDecode(token);
  const isAboutToExpire =
    new Date(jwt.exp * 1000 - deltaMinutes * 60 * 1000) < new Date().getTime();
  return isAboutToExpire;
};

export const getConversationSource = () => {
  if (isExternalSession()) {
    return CONVERSATION_SOURCES.INBOX_PODS;
  }

  return CONVERSATION_SOURCES.INBOX;
};

export const getPriorityColor = color => {
  const theme = getTheme();
  if (!color) {
    return theme.colors.priority.default;
  }
  const isHexColor = /^#[0-9A-F]{6}[0-9a-f]{0,2}$/i.test(color);
  if (isHexColor) {
    return color;
  }

  return theme.colors.priority[color] || color.toLowerCase();
};
